import React, {
  FunctionComponent,
  useCallback,
  useState,
  ChangeEvent,
  useEffect,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';

import addressApi from 'scenes/common/api/address';
import { SubmitButton } from 'components';
import { FormGroup, Button } from 'components/chroma';
import {
  AutocompleteAddress,
  CANNOT_FIND_ADDRESS,
} from 'components/chroma/extended';

import { ApiData, PostalAddress, Address } from 'scenes/common/types';
import {
  makeApiSuccess,
  isApiError,
  getApiData,
} from 'scenes/common/helpers/apiData';
import {
  AddressNotFoundModal,
  ScreenReaderAddressInstruction,
} from 'scenes/common/components/Steps/Addresses/components';
import { useDebouncedCallback } from 'use-debounce/lib';
import { AddressStepProps } from '../types';
import {
  DEBOUNCE_MS,
  MIN_LENGTH,
  validToLookup,
} from '../helpers/addressHelpers';

export const QUESTION = 'What is the postal address?';
export const ERROR_MESSAGE = 'Please select your postal address';

const POSTAL_ADDRESS = 'postalAddress';
const ADDRESS_ERROR = 'address-error';

const IDLE = makeApiSuccess(undefined);

type AddressOptionType = {
  label: string;
  value: Address;
};

const ManualSelectionButtonWrapper = styled.div`
  text-align: center;
`;

const displayAddress = (address: PostalAddress | undefined) =>
  address ? address.address.replace(/\n/g, ', ') : '';

const toAddressOptions = (
  addresses: ApiData<Address[] | undefined>
): AddressOptionType[] | undefined => {
  const data = getApiData(addresses);
  if (!data) {
    return undefined;
  }

  if (data.length > 0) {
    return data.map((address: Address) => ({
      label: displayAddress(address),
      value: address,
    }));
  }
  return [];
};

const DifferentPostalAddressStep: FunctionComponent<AddressStepProps<
  PostalAddress
>> = ({ defaultStateValue, onNext, onClickCallUs, onApiError }) => {
  const {
    formState: hooksForm,
    setError,
    getValues,
    clearErrors,
    handleSubmit,
    errors,
    control,
  } = useForm({
    mode: 'onChange',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [addressList, setAddressList] = useState<
    ApiData<Address[] | undefined>
  >(IDLE);
  const [showModal, setShowModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<
    PostalAddress | undefined
  >(defaultStateValue);

  const onSubmit = useCallback(() => {
    onNext({
      isPostalAddressDifferentManual: false,
      postalAddress: selectedAddress,
    });
  }, [onNext, selectedAddress]);

  const goToAddressManualSelect = useCallback(() => {
    onNext({
      isPostalAddressDifferentManual: true,
    });
  }, [onNext]);

  const setAddressError = useCallback(
    (message: string = ''): void => {
      setError(ADDRESS_ERROR, {
        type: 'manual',
        message,
      });
    },
    [setError]
  );

  const debounceSetIsLoading = useDebouncedCallback(
    (value: boolean) => setIsLoading(value),
    DEBOUNCE_MS
  );

  const handleChange = useCallback(
    async (value: string, onChange: Function): Promise<void> => {
      onChange(value);
      if (validToLookup(value)) {
        debounceSetIsLoading(true);
      }
    },
    [debounceSetIsLoading]
  );

  const handleClear = useCallback(
    (onChange: Function): void => {
      onChange('');
      clearErrors();
      setAddressList(IDLE);
    },
    [clearErrors]
  );

  const handleSelect = useCallback(
    (addressOption: AddressOptionType, onChange: Function): void => {
      if (addressOption.label === CANNOT_FIND_ADDRESS) {
        setShowModal(true);
      } else {
        onChange(addressOption.label);
        clearErrors();
        setAddressList(IDLE);
        setSelectedAddress(addressOption.value);
      }
    },
    [clearErrors]
  );

  const handleBlur = useCallback(
    (value: string): void => {
      const errorMessage = value?.length >= MIN_LENGTH ? ERROR_MESSAGE : '';
      setAddressError(errorMessage);
      setAddressList(IDLE);
    },
    [setAddressError]
  );

  useEffect(() => {
    let componentMounted = true;

    if (isLoading) {
      setAddressError();
      setSelectedAddress(undefined);
      addressApi()
        .getAddresses(getValues(POSTAL_ADDRESS))
        .then((addresses) => {
          if (isApiError(addresses)) {
            onApiError(`Address api: ${addresses.errorReason?.message || ''}`);
          }
          if (componentMounted) {
            setAddressList(
              isApiError(addresses) ? makeApiSuccess([]) : addresses
            );
            setIsLoading(false);
          }
        });
    }

    return () => {
      componentMounted = false;
    };
  }, [getValues, isLoading, onApiError, setAddressError]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ScreenReaderAddressInstruction items={getApiData(addressList)} />
      <Controller
        control={control}
        name={POSTAL_ADDRESS}
        defaultValue={displayAddress(defaultStateValue)}
        rules={{ required: true, minLength: MIN_LENGTH }}
        render={({ onChange, value }) => (
          <FormGroup
            id={POSTAL_ADDRESS}
            label={QUESTION}
            error={errors[ADDRESS_ERROR]?.message || ''}
          >
            <AutocompleteAddress
              id={POSTAL_ADDRESS}
              name={POSTAL_ADDRESS}
              placeholder="e.g. Unit 214 32 Sample St Suburb"
              ariaLabel="Different Postal Address"
              value={value}
              values={toAddressOptions(addressList)}
              valid={Boolean(value) && hooksForm.isValid}
              error={Boolean(value) && !hooksForm.isValid}
              loading={isLoading}
              autoComplete="on"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleChange(event.target.value, onChange)
              }
              onClear={() => handleClear(onChange)}
              onSelect={(_: Event, listItem: AddressOptionType) =>
                handleSelect(listItem, onChange)
              }
              onBlur={() => handleBlur(value)}
            />
          </FormGroup>
        )}
      />
      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
      <ManualSelectionButtonWrapper>
        <Button
          secondary
          maxWidth
          margin={{ top: 3 }}
          onClick={goToAddressManualSelect}
        >
          Enter address manually
        </Button>
      </ManualSelectionButtonWrapper>
      <AddressNotFoundModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onClickCallUs={onClickCallUs}
      />
    </form>
  );
};

export default DifferentPostalAddressStep;
